import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';

import LanguageIcon from '@mui/icons-material/Language';
import OutputIcon from '@mui/icons-material/Output';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatIcon from '@mui/icons-material/Chat';
import TuneIcon from '@mui/icons-material/Tune';

import { themeColors, shadows } from '../../styles/theme';
import { NODE_CONFIG, NODE_TYPES, OUTPUT_FORMAT_CONFIG } from '../../config/nodeConfig';

export default function ModernFlowSidebar({
  onAddNode,
  project,
  onUpdateProject,
  onLanguagesChange,
  onInstructionsClick,
  onChatPromptClick,
  selectedLanguages = [],
  width = 280,
  onResize,
  nodes = [],
}) {
  const isStateful = project?.mode === 'stateful_job';

  const nodeCategories = [
    {
      name: 'Input',
      color: themeColors.primary[500],
      nodes: isStateful
        ? [NODE_TYPES.INPUT, NODE_TYPES.PHOTO, NODE_TYPES.PDF, NODE_TYPES.STATE_HISTORY]
        : [NODE_TYPES.INPUT, NODE_TYPES.PHOTO, NODE_TYPES.PDF],
    },
    {
      name: 'Processing',
      color: themeColors.primary[600],
      nodes: [NODE_TYPES.AI, NODE_TYPES.CODE, NODE_TYPES.TEMPLATE],
    },
    {
      name: 'Output',
      color: themeColors.primary[700],
      nodes: [NODE_TYPES.OUTPUT],
    },
  ];

  const renderNodeButton = (nodeType) => {
    const config = NODE_CONFIG[nodeType];
    if (!config) return null;

    const Icon = config.icon;

    // Check if this node type already exists (unique source nodes)
    const isUnique = nodeType === NODE_TYPES.INPUT || nodeType === NODE_TYPES.PHOTO || nodeType === NODE_TYPES.PDF || nodeType === NODE_TYPES.STATE_HISTORY;
    const alreadyExists = isUnique && nodes.some((node) => node.type === nodeType);

    return (
      <Button
        key={nodeType}
        onClick={() => onAddNode(nodeType)}
        size="sm"
        variant="plain"
        disabled={alreadyExists}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          px: 1.5,
          py: 1,
          backgroundColor: 'white',
          border: `1px solid ${themeColors.neutral[200]}`,
          borderRadius: 1.5,
          color: themeColors.neutral[800],
          fontWeight: 500,
          fontSize: '0.875rem',
          transition: 'all 0.15s ease',
          opacity: alreadyExists ? 0.4 : 1,
          cursor: alreadyExists ? 'not-allowed' : 'pointer',
          '&:hover': {
            backgroundColor: alreadyExists ? 'white' : 'white',
            borderColor: alreadyExists ? themeColors.neutral[200] : config.colors.border,
          },
        }}
      >
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${config.colors.border}15`,
            mr: 1.5,
          }}
        >
          <Icon sx={{ fontSize: 14, color: config.colors.border }} />
        </Box>
        {config.label}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        width: width,
        height: '100%',
        backgroundColor: 'white',
        borderRight: `1px solid ${themeColors.neutral[200]}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: themeColors.neutral[25],
          borderBottom: `1px solid ${themeColors.neutral[200]}`,
        }}
      >
        <Typography
          level="title-sm"
          sx={{
            fontWeight: 700,
            color: themeColors.neutral[900],
            fontSize: '0.8125rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Flow Builder
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Nodes Section */}
        <Box sx={{ p: 3 }}>
          <Typography
            level="body-xs"
            sx={{
              color: themeColors.neutral[600],
              fontWeight: 600,
              mb: 2,
              fontSize: '0.75rem',
            }}
          >
            ADD NODES
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nodeCategories.map((category) => (
              <Box key={category.name}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 3,
                      height: 12,
                      borderRadius: 1,
                      backgroundColor: category.color,
                    }}
                  />
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.neutral[700],
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  >
                    {category.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {category.nodes.map(renderNodeButton)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Settings Section */}
        <Box sx={{ p: 3 }}>
          <Typography
            level="body-xs"
            sx={{
              color: themeColors.neutral[600],
              fontWeight: 600,
              mb: 2,
              fontSize: '0.75rem',
            }}
          >
            FLOW SETTINGS
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Output Format */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <OutputIcon sx={{ fontSize: 14, color: themeColors.neutral[600] }} />
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    color: themeColors.neutral[700],
                    fontSize: '0.75rem',
                  }}
                >
                  Default Output
                </Typography>
              </Box>
              <Select
                value={project?.default_export_format || 'docx'}
                onChange={(e, value) => {
                  if (onUpdateProject) {
                    onUpdateProject({ default_export_format: value });
                  }
                }}
                size="sm"
                sx={{
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  backgroundColor: 'white',
                  fontSize: '0.8125rem',
                  '&:hover': {
                    borderColor: themeColors.neutral[300],
                  },
                  '--Select-focusedThickness': '0px',
                  '&:focus-within': {
                    borderColor: themeColors.primary[500],
                  },
                }}
              >
                {Object.entries(OUTPUT_FORMAT_CONFIG).map(([key, config]) => (
                  <Option key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: 0.5,
                          backgroundColor: `${config.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {config.icon({ sx: { fontSize: 11, color: config.color } })}
                      </Box>
                      <Typography level="body-sm" sx={{ fontSize: '0.8125rem' }}>
                        {config.label}
                      </Typography>
                    </Box>
                  </Option>
                ))}
              </Select>
            </Box>

            {/* Mode */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TuneIcon sx={{ fontSize: 14, color: themeColors.neutral[600] }} />
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    color: themeColors.neutral[700],
                    fontSize: '0.75rem',
                  }}
                >
                  Mode
                </Typography>
              </Box>
              <Select
                value={project?.mode || 'stateless'}
                onChange={(e, value) => {
                  if (onUpdateProject) {
                    onUpdateProject({ mode: value });
                  }
                }}
                size="sm"
                sx={{
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  backgroundColor: 'white',
                  fontSize: '0.8125rem',
                  '&:hover': {
                    borderColor: themeColors.neutral[300],
                  },
                  '--Select-focusedThickness': '0px',
                  '&:focus-within': {
                    borderColor: themeColors.primary[500],
                  },
                }}
              >
                <Option value="stateless">
                  <Typography level="body-sm" sx={{ fontSize: '0.8125rem' }}>Stateless</Typography>
                </Option>
                <Option value="stateful_job">
                  <Typography level="body-sm" sx={{ fontSize: '0.8125rem' }}>Stateful (job)</Typography>
                </Option>
              </Select>
            </Box>

            {/* Languages */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LanguageIcon sx={{ fontSize: 14, color: themeColors.neutral[600] }} />
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    color: themeColors.neutral[700],
                    fontSize: '0.75rem',
                  }}
                >
                  Languages
                </Typography>
              </Box>
              <Button
                onClick={onLanguagesChange}
                size="sm"
                variant="outlined"
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  backgroundColor: 'white',
                  color: themeColors.neutral[700],
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  '&:hover': {
                    borderColor: themeColors.neutral[300],
                    backgroundColor: 'white',
                  },
                }}
              >
                {selectedLanguages?.length > 0
                  ? `${selectedLanguages.length} selected`
                  : 'Configure'}
              </Button>
            </Box>

            {/* Instructions */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <DescriptionIcon sx={{ fontSize: 14, color: themeColors.neutral[600] }} />
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    color: themeColors.neutral[700],
                    fontSize: '0.75rem',
                  }}
                >
                  Instructions
                </Typography>
              </Box>
              <Button
                onClick={onInstructionsClick}
                size="sm"
                variant="outlined"
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  backgroundColor: 'white',
                  color: themeColors.neutral[700],
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  '&:hover': {
                    borderColor: themeColors.neutral[300],
                    backgroundColor: 'white',
                  },
                }}
              >
                {project?.instructions ? 'Edit' : 'Add'}
              </Button>
            </Box>

            {/* Chat Prompt */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ChatIcon sx={{ fontSize: 14, color: themeColors.neutral[600] }} />
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    color: themeColors.neutral[700],
                    fontSize: '0.75rem',
                  }}
                >
                  BI Context
                </Typography>
              </Box>
              <Button
                onClick={onChatPromptClick}
                size="sm"
                variant="outlined"
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  backgroundColor: 'white',
                  color: themeColors.neutral[700],
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  '&:hover': {
                    borderColor: themeColors.neutral[300],
                    backgroundColor: 'white',
                  },
                }}
              >
                {project?.bi_agent_context ? 'Edit' : 'Add'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Resize Handle */}
      <Box
        onMouseDown={onResize}
        sx={{
          position: 'absolute',
          top: 0,
          right: -2,
          width: 4,
          height: '100%',
          cursor: 'ew-resize',
          zIndex: 10,
          '&:hover': {
            backgroundColor: themeColors.primary[500],
          },
          transition: 'background-color 0.15s ease',
        }}
      />
    </Box>
  );
}
